import React, {useEffect, useMemo} from "react";
import {observer} from "mobx-react-lite";

import {
    MapContainer,
    Marker,
    TileLayer,
    useMap,
    useMapEvents,
} from "react-leaflet";

import L from "leaflet";

import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import appStore from "../storage/AppStore";
import systemStore from "../storage/SystemStore";

import "leaflet/dist/leaflet.css";


const mapMarkerIcon = new L.Icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,

    iconSize: [25, 41],
    iconAnchor: [12, 41],

    popupAnchor: [1, -34],

    shadowSize: [41, 41],
});


const toCoordinate = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return null;
    }

    const number = Number(value);

    return Number.isFinite(number)
        ? number
        : null;
};


const normalizePoint = (value) => {
    const lat =
        toCoordinate(value?.lat);

    const lng =
        toCoordinate(value?.lng);


    if (
        lat === null ||
        lng === null ||
        lat < -90 ||
        lat > 90 ||
        lng < -180 ||
        lng > 180
    ) {
        return null;
    }


    return {
        lat,
        lng,
    };
};


const Recenter = ({
    center,
}) => {

    const map =
        useMap();


    useEffect(() => {
        if (!center) {
            return;
        }

        map.setView(
            [
                center.lat,
                center.lng,
            ],

            map.getZoom(),

            {
                animate: false,
            }
        );

    }, [
        center?.lat,
        center?.lng,
        map,
    ]);


    return null;
};


const MapClickHandler = ({
    disabled,
    onChange,
}) => {

    useMapEvents({
        click(event) {
            if (disabled) {
                return;
            }

            onChange({
                lat:
                    event.latlng.lat,

                lng:
                    event.latlng.lng,
            });
        },
    });


    return null;
};


const ClickableMap = ({
    center,

    zoom = 13,
    height = 300,

    store,
    prop,

    disabled = false,

    onChange,
}) => {

    const point =
        normalizePoint(
            store?.[prop]
        );


    const normalizedCenter =
        useMemo(() => {

            return (
                normalizePoint(center) ||
                point ||
                {
                    lat: 0,
                    lng: 0,
                }
            );

        }, [
            center?.lat,
            center?.lng,

            point?.lat,
            point?.lng,
        ]);


    const isDisabled =
        disabled ||
        systemStore.loading;


    const invalid =
        Boolean(
            store?.was_validated &&
            !point
        );


    const setPoint = (value) => {
        if (isDisabled) {
            return;
        }


        const nextPoint =
            normalizePoint(value);


        if (!nextPoint) {
            return;
        }


        store?.setProperty?.(
            prop,
            nextPoint
        );


        onChange?.(
            nextPoint
        );
    };


    return (
        <>
            {invalid && (
                <small className="p-error block mb-2">
                    {
                        appStore.tr.select_point_on_map ||
                        "Select point on map"
                    }
                </small>
            )}


            <MapContainer
                center={[
                    normalizedCenter.lat,
                    normalizedCenter.lng,
                ]}

                zoom={zoom}

                scrollWheelZoom={false}

                style={{
                    height,
                    width: "100%",
                }}
            >

                <Recenter
                    center={
                        normalizedCenter
                    }
                />


                <TileLayer
                    attribution="&copy; OpenStreetMap contributors"

                    url={
                        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    }
                />


                <MapClickHandler
                    disabled={
                        isDisabled
                    }

                    onChange={
                        setPoint
                    }
                />


                {point && (
                    <Marker
                        position={[
                            point.lat,
                            point.lng,
                        ]}

                        icon={
                            mapMarkerIcon
                        }

                        draggable={
                            !isDisabled
                        }

                        eventHandlers={{
                            dragend: (
                                event
                            ) => {
                                const value =
                                    event.target
                                        .getLatLng();


                                setPoint({
                                    lat:
                                        value.lat,

                                    lng:
                                        value.lng,
                                });
                            },
                        }}
                    />
                )}

            </MapContainer>
        </>
    );
};


export default observer(ClickableMap);